/** @file The layers of an import, bottom to top: checkbox, size and offset; x/y fields for placed PNGs. */
import type { Translate } from '@pose2d/i18n/types';
import { NumberField } from '../ui-kit/NumberField';
import type { ImportDraft, ImportSession, LayerChoice } from './importTypes';

/** Props of LayerChoiceList. */
export interface LayerChoiceListProps {
  t: Translate;
  draft: ImportDraft;
  session: Pick<ImportSession, 'toggle' | 'move'>;
}

function LayerOffset({ t, choice, onMove }: { t: Translate; choice: LayerChoice; onMove: ImportSession['move'] }) {
  return (
    <span className="import-layer-offset">
      <NumberField label={t('psdImport.x')} value={choice.x} precision={0} onCommit={(x) => { onMove(choice.index, x, choice.y); }} />
      <NumberField label={t('psdImport.y')} value={choice.y} precision={0} onCommit={(y) => { onMove(choice.index, choice.x, y); }} />
    </span>
  );
}

/** PSD offsets are read-only; PNG offsets become editable once placement is switched on. */
export function LayerChoiceList({ t, draft, session }: LayerChoiceListProps) {
  const editable = draft.source.kind === 'png' && draft.place;
  return (
    <fieldset className="import-layers">
      <legend>{t('psdImport.layers')}</legend>
      {draft.choices.map((c) => (
        <div key={c.index} className="import-layer">
          <label>
            <input type="checkbox" checked={c.checked} onChange={(e) => { session.toggle(c.index, e.currentTarget.checked); }} />
            <span className="import-layer-name" title={c.path}>{c.name}</span>
          </label>
          <span className="import-layer-size">{t('psdImport.size', { w: c.width, h: c.height, x: c.x, y: c.y })}</span>
          {c.hidden ? <span className="import-layer-hidden">{t('psdImport.hidden')}</span> : null}
          {editable ? <LayerOffset t={t} choice={c} onMove={session.move} /> : null}
        </div>
      ))}
    </fieldset>
  );
}
